import React, { useContext, useState } from 'react';
import _ from 'underscore';
import { getLayoutSize } from '@bingads-webui-fluent-component/layout-size';
import { GlobalNavContainer, Spacer, NavItem } from '@bingads-webui-fluent-component/global-menu-container';
import { makeStyles, mergeClasses, shorthands } from '@fluentui/react-components';
import { HelpMenu } from '@bingads-webui-react/help-menu';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { FeedbackPanel } from '@bingads-webui-react/feedback-panel';

import { Branding } from './branding';
import { MeControlItem } from './me-control-item';
import { NotificationItem } from './notification-item';
import { NotificationPlaceHolderItem } from './notification-placeholder';
import { AccountsItem } from './accounts-item';
import { CopilotRegular } from './copilot-icon';
import { Search } from './search';
import type { II18n, GlobalMenuProps } from './types';

const useStyles = makeStyles({
  container: {
    width: '100%',
    paddingRight: '0px',
    paddingLeft: '12px',
    ...shorthands.borderBottom('1px', 'solid', 'rgba(0, 0, 0, 0.04)'),
  },
});

const noCustomerScopeHideOverrides = {
  accountBreadcrumb: true,
  customerSelector: true,
  import: true,
  reports: true,
  searchBar: true,
  tools: true,
  mmc: true,
  myWebsite: true,
};

const adjustHideForScope = ({ currentUser, originalHide }) => ({
  ...originalHide,
  ...(!_.result(currentUser.CurrentCustomer, 'Id') ? noCustomerScopeHideOverrides : {}),
});

const defaultTestAppId = 50025;
const defaultAppId = 2482;

export const GlobalMenu: React.FC<GlobalMenuProps> = ({
  feedbackProps,
  hide: originalHide,
  perfMarker,
  supportUrl,
  serverContext,
  settingsContext,
  NotificationsBar,
  account,
  onCopilotClick,
  onLogoClick,
  renderSearchResults,
  siteMap,
}) => {
  const classes = useStyles();
  const i18n: II18n = useContext(I18nContext);
  const [isNotificationActive, setIsNotificationActive] = useState(false);
  const [isFeedbackPanelOpen, setIsFeedbackPanelOpen] = useState(false);
  const currentUser = {
    ...serverContext.CurrentUser,
    CurrentCustomer: serverContext.CurrentCustomer,
    FullName: serverContext.CurrentUser.Name,
  };
  const hide = adjustHideForScope({ currentUser, originalHide });
  const {
    isTestEnv,
    email,
    pageId,
    sessionId,
    feedbackLogger,
    appId,
    featureArea,
  } = feedbackProps;

  const closeFeedbackPanel = () => setIsFeedbackPanelOpen(false);

  return (
    <>
      <GlobalNavContainer
        className={
          // help popup requires container-fluid class for global menu.
          mergeClasses(classes.container, 'container-fluid', 'new-global-menu')
        }
        getLayoutSize={getLayoutSize}
      >
        <Branding itemKey="branding" isPined onLogoClick={onLogoClick} />
        {!hide.accountBreadcrumb
          && (
            <AccountsItem
              itemKey="accounts"
              account={account}
              perfMarker={perfMarker}
            />
          )}
        <Spacer itemKey="spacer1" />
        {!hide.searchBar
          && (
            <Search
              itemKey="search"
              renderSearchResults={renderSearchResults}
              serverContext={serverContext}
              siteMap={siteMap}
              onCopilotClick={onCopilotClick}
            />
          )}
        {!hide.copilot
          && (
            <NavItem
              className="new-global-menu-copilot-button"
              itemKey="copilot-chat"
              icon={CopilotRegular}
              text={i18n.getString(_TL_('Copilot'))}
              onClick={onCopilotClick}
            />
          )}
        {!hide.notificationBar
          && <NotificationItem className="notifications-item" itemKey="notifications" isActive={isNotificationActive} />
        }
        {!hide.notificationBar
          && (
            <NotificationPlaceHolderItem
              itemKey="notification-placeholder"
              NotificationsBar={NotificationsBar}
              isPined
              setIsActive={(isActive: boolean) => setIsNotificationActive(isActive)}
            />
          )}
        {!hide.helpBar
          && (
            <HelpMenu
              setIsFeedbackPanelOpen={setIsFeedbackPanelOpen}
              isFeedbackPanelOpen={isFeedbackPanelOpen}
              isCopilotDisabled={hide.copilot}
              itemKey="help"
            />
          )}
        {!hide.meControl
          && (
            <MeControlItem
              supportUrl={supportUrl}
              itemKey="meControl"
              isPined
              hide={hide}
              currentUser={currentUser}
              perfMarker={perfMarker}
              settingsContext={settingsContext}
            />
          )}
      </GlobalNavContainer>
      {
        isFeedbackPanelOpen
        && (
          <FeedbackPanel
            appId={appId || (isTestEnv ? defaultTestAppId : defaultAppId)}
            isTestEnv={isTestEnv}
            isOpen={isFeedbackPanelOpen}
            dismissPanel={closeFeedbackPanel}
            i18n={i18n}
            instrumentationService={feedbackLogger || { log: () => { } }}
            email={email}
            pageId={pageId}
            sessionId={sessionId}
            featureArea={featureArea}
            appData={{ NavigationRefresh: true }}
          />
        )
      }
    </>
  );
};
